import { SeoData } from '@/types/seo';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Fragment, useMemo } from 'react';
type SEO = {
  title: string;
  description: string;
};
interface SeoProps {
  seo?: SeoData;
  host?: string;
}
const SEO_PATHNAME_MAPPING: { [key: string]: SEO } = {
  '/payment/success': {
    title: 'Payment success | Eureka Forbes',
    description: 'Payment success - Eureka forbes',
  },
  '/service': {
    title: 'Service | Eureka Forbes',
    description: 'Scheduling Allocation - Eureka forbes',
  },
  '/cart': {
    title: 'Cart | Eureka Forbes',
    description: 'Cart - Eureka forbes',
  },
  '/amc/cc/agent': {
    title: 'Call Centre | Eureka forbes',
    description: 'Eureka forbes',
  },
  '/': {
    title: 'Home | Eureka forbes',
    description: 'Eureka forbes',
  },
  default: {
    title: 'Eureka forbes',
    description: 'Eureka forbes',
  },
};
const Seo = ({ seo, host }: SeoProps) => {
  const router = useRouter();
  const { pathname } = router;
  const { title: defaultTitle, description: defaultDescription } =
    SEO_PATHNAME_MAPPING[pathname] || SEO_PATHNAME_MAPPING.default;
  const {
    metaSocial = [],
    metaDescription,
    metaRobots,
    metaTitle,
    keywords,
    structuredData = {},
    canonicalURL,
    metaImage,
    metaViewport,
  } = seo ?? {};
  const title = metaTitle || defaultTitle;
  const description = metaDescription || defaultDescription;
  const image = metaImage; //dlv(metaImage, 'data.attributes.formats.thumbnail.url');
  const SD_KEYS = Object.keys(structuredData || {});
  const metaTags = useMemo(
    () => [
      {
        name: 'description',
        content: description,
      },
      {
        name: 'keywords',
        content: keywords || '',
      },
      {
        name: 'robots',
        content: metaRobots || '',
      },
      {
        name: 'twitter:card',
        content: 'product',
      },
      {
        name: 'twitter:site',
        content: '@EurekaForbes',
      },
      {
        name: 'twitter:description',
        content: description,
      },
      {
        name: 'twitter:app:name:iphone',
        content: 'Eureka Forbes',
      },
      {
        name: 'twitter:app:id:iphone',
        content: '1463742085',
      },
      {
        name: 'twitter:app:name:ipad',
        content: 'Eureka Forbes',
      },
      {
        name: 'twitter:app:id:ipad',
        content: '',
      },
      {
        name: 'twitter:app:name:googleplay',
        content: 'Eureka Forbes',
      },
      {
        name: 'twitter:app:id:googleplay',
        content: 'com.efl.eurekaforbes',
      },
      {
        name: 'twitter:app:country',
        content: 'India',
      },
      {
        property: 'og:title',
        content: title,
      },
      {
        property: 'og:site_name',
        content: 'Eureka Forbes',
      },
      {
        property: 'og:locale',
        content: 'en_IN',
      },
      {
        property: 'og:description',
        content: description,
      },
      {
        property: 'og:url',
        content: pathname,
      },
      {
        property: 'og:type',
        content: 'product',
      },
      {
        property: 'og:image',
        content: image,
      },
      {
        property: 'image',
        content: image,
      },
    ],
    [description, image, keywords, metaRobots, pathname, title],
  );
  return (
    <Head>
      <title>{title}</title>
      <link rel="icon" href="/images/favicon.ico" />
      {metaTags.map(({ property, content, name }) => {
        const key = name || property;
        if (!content) return <Fragment key={key}>{null}</Fragment>;
        return <meta key={key} name={name} property={property} content={content} />;
      })}
      {metaSocial.map(({ socialNetwork, title, description, image }: any) => {
        const network = socialNetwork.toLowerCase();
        return (
          <Fragment key={network}>
            <meta name={`${network}:title`} content={title} />
            <meta name={`${network}:description`} content={description} />
            <meta name={`${network}:image`} content={image || metaImage} />
          </Fragment>
        );
      })}
      {canonicalURL ? <link rel="canonical" href={canonicalURL} /> : null}
      <meta
        name="viewport"
        content={metaViewport || 'width=device-width, initial-scale=1, user-scalable=no'}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Eureka Forbes Limited',
            url: 'https://www.eurekaforbes.com',
            logo: 'https://www.eurekaforbes.com/images/favicon.ico',
            alternateName: 'Eureka Forbes',
            sameAs: [
              'https://www.facebook.com/EurekaForbes',
              'https://www.instagram.com/eurekaforbesofficial?igsh=NGhtcm14dno4dzJy',
              'https://twitter.com/EurekaForbes',
              'https://www.linkedin.com/company/eureka-forbes-ltd/',
              'https://www.youtube.com/@eurekaforbes',
            ],
            email: 'customercare@eurekaforbes.com',
            contactPoint: [
              {
                '@type': 'ContactPoint',
                telephone: '7039883333',
                contactType: 'customer service',
                areaServed: 'IN',
                availableLanguage: 'en',
              },
            ],
          }),
        }}
      />

      {SD_KEYS.map((key, index) => (
        <script
          key={index}
          type="application/ld+json"
          className={`https://schema.org/${key}`}
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData[key]) }}
        />
      ))}
    </Head>
  );
};
export default Seo;
